import type { GeneralParameters } from '../../general/parameters/general'
import type { PlayerOpenParameters } from '../parameters/player-open'

import { Analytix } from '~/shared/analytix'
import { AnalytixEvent } from '../../general/event'
import { Gate, seeds } from '../../general/gate'
import { logger } from '../index.h'
import { parameters } from '../parameters/player-open'
import { getActivePlayerState } from '../players'

/**
 * Queue event wrapper for `player_open`.
 *
 * This event is intentionally defensive:
 * - waits for async metadata,
 * - debounces flapping updates,
 * - force-unblocks after timeout to avoid deadlocking player group.
 */
export class PlayerOpenEvent extends AnalytixEvent<PlayerOpenParameters> {
  name = 'player_open'
  group = 'player'
  parameters = parameters
  sender = Analytix.prototype.sendPlayerOpenEvent

  // `player_open` event uses session gate to ensure that it is not emitted before session is started
  // `player_open` event opens player gate
  gates = [Gate.Uses(seeds.session), Gate.Opens(seeds.player)]

  // coalesce multiple consecutive `player_open` events
  override coalescing = true

  // infinite expiration event timeout because `player_open` event should not expire,
  // but we will force it to be ready after certain `forceReadyTimeout` time
  // to avoid blocking event indefinitely in case of missing parameters
  override timeout = Infinity

  forceReady = false
  forceReadyTimeout = 10000 // 10s
  forceReadyTimeoutId?: ReturnType<typeof setTimeout>

  // after all parameters are set and valid - wait a bit just in case, to make sure
  // that all consecutive parameters updates are processed before sending the event
  postponed = false
  postponeTimeout = 3000 // 3s
  postponeTimeoutId?: ReturnType<typeof setTimeout>

  constructor() {
    super()

    // `player_open` event is triggered on player instantiation, but the parameters
    // are set asynchronously after fetching content metadata. To ensure that the event
    // is sent only when all required parameters are available, we listen to updates
    // on the parameters and re-verify event readiness.
    this.verify().then((ready) => {
      if (!ready) {
        const off = this.parameters.updates.on(() => this.verify())

        // force ready after timeout to avoid blocking event indefinitely in case of missing parameters
        this.forceReadyTimeoutId = setTimeout(() => {
          if (!this.ready) {
            logger.warn(`force ready "player_open" event after ${this.forceReadyTimeout}ms timeout`) // prettier-ignore
            this.forceReady = true
            this.verify()
          }
        }, this.forceReadyTimeout)

        // cleanup listener and timeouts on readiness
        this.readiness.finally(() => {
          off()
          clearTimeout(this.forceReadyTimeoutId)
          clearTimeout(this.postponeTimeoutId)
        })
      }
    })
  }

  validate(): false | [GeneralParameters, PlayerOpenParameters] {
    const playerId = this.parameters.get('(playerId)')
    const player = getActivePlayerState(playerId)

    // valid conditions for `player_open` event:
    // - `contentUrl`, `contentName`, and `programName` parameters are available,
    //    which means that content metadata has been fetched and parameters are set
    const valid =
      this.parameters.get('contentUrl') != null &&
      this.parameters.get('contentName') != null &&
      this.parameters.get('programName') != null

    // in case of missing `contentUrl` from React component, try to get it from player instance
    if (this.forceReady && this.parameters.get('contentUrl') == null) {
      if (player) {
        if (player.src) this.parameters.set('contentUrl', player.src)
      } else {
        this.abort() // no actual player instance rendered or visible
      }
    }

    // if the event is valid or forced to be ready, and player instance is available,
    // consider the event ready to be sent
    const overallValid = (valid || this.forceReady) && player != null

    // postpone sending the event if it just became valid
    // to allow any consecutive parameters updates to be processed
    // this works like debounce mechanism, so it will postpone sending
    // the event until parameters stabilize for certain period of time
    //
    // when timer never started — first time becoming overall valid, start the debounce timer
    // when timer is running — parameter update arrived mid-debounce, restart the timer
    // when timer fired, debounce settled — fall through and return the snapshot
    if (overallValid) {
      if (!this.postponeTimeoutId || this.postponed) {
        this.postponed = true
        clearTimeout(this.postponeTimeoutId)
        this.postponeTimeoutId = setTimeout(() => {
          this.postponed = false
          this.verify()
        }, this.postponeTimeout)
        return false
      }
    } else {
      // not valid anymore: cancel any outstanding postpone timer and reset state
      clearTimeout(this.postponeTimeoutId)
      this.postponeTimeoutId = undefined
      this.postponed = false
      return false
    }

    return [
      Object.fromEntries(this.general),
      Object.fromEntries(this.parameters),
    ]
  }
}
